import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const strength = getStrength(next);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New passwords do not match");
    if (next.length < 8) return toast.error("Password must be at least 8 characters");
    if (next === current) return toast.error("New password must be different from current");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
      if (data.token) localStorage.setItem("sk_token", data.token);
      toast.success("Password updated");
      setDone(true);
      setCurrent(""); setNext(""); setConfirm("");
      setTimeout(() => setDone(false), 5000);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="change-password-card" className="bg-white border border-[#EAEAEA] rounded-xl p-6 mt-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
          <KeyRound size={16} className="text-[#8A6E1E]"/>
        </div>
        <div>
          <h2 className="font-display text-xl">Change password</h2>
          <p className="text-xs text-black/50">Use a strong password. Minimum 8 characters.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4 relative">
        <Field label="Current password" value={current} onChange={setCurrent} testid="cp-current" show={show} onToggle={() => setShow(!show)} />
        <div className="hidden sm:block"/>
        <Field label="New password (min 8)" value={next} onChange={setNext} testid="cp-new" show={show} />
        <Field label="Confirm new password" value={confirm} onChange={setConfirm} testid="cp-confirm" show={show} match={next && confirm && next === confirm} />

        {next && (
          <div className="sm:col-span-2">
            <div className="flex items-center gap-2 text-xs">
              <div className="flex-1 h-1.5 rounded-full bg-black/5 overflow-hidden">
                <div className={`h-full transition-all ${strength.color}`} style={{ width: `${strength.pct}%` }}/>
              </div>
              <span className={strength.textColor}>{strength.label}</span>
            </div>
          </div>
        )}

        <div className="sm:col-span-2 flex items-center justify-between mt-2">
          {done ? (
            <span className="inline-flex items-center gap-2 text-emerald-700 text-sm" data-testid="cp-success">
              <CheckCircle2 size={16}/> Password updated
            </span>
          ) : <span/>}
          <button data-testid="cp-submit" disabled={busy || !current || !next || !confirm} className="sk-btn-gold disabled:opacity-50">
            {busy ? "Updating..." : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid, show, onToggle, match }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-black/50 mb-1">{label}</div>
      <div className="relative">
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full border rounded-lg px-3 py-2 pr-10 focus:border-[#D4AF37] outline-none ${match ? "border-emerald-400" : "border-[#EAEAEA]"}`}
          autoComplete="new-password"
        />
        {onToggle && (
          <button type="button" onClick={onToggle} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-black/40 hover:text-black/80" tabIndex={-1}>
            {show ? <EyeOff size={16}/> : <Eye size={16}/>}
          </button>
        )}
      </div>
    </label>
  );
}

function getStrength(pw) {
  if (!pw) return { pct: 0, label: "", color: "bg-black/10", textColor: "text-black/40" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const levels = [
    { pct: 20, label: "Very weak", color: "bg-red-500", textColor: "text-red-600" },
    { pct: 40, label: "Weak", color: "bg-orange-500", textColor: "text-orange-600" },
    { pct: 60, label: "Fair", color: "bg-yellow-500", textColor: "text-yellow-700" },
    { pct: 80, label: "Strong", color: "bg-lime-500", textColor: "text-lime-700" },
    { pct: 100, label: "Very strong", color: "bg-emerald-500", textColor: "text-emerald-700" },
  ];
  return levels[Math.max(0, Math.min(4, score - 1))];
}
